import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, UI, DEPTH } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';
import { InputRouter, PlayerId } from '../systems/InputRouter';
import { AudioRouter } from '../systems/AudioRouter';
import { SettingsState } from '../state/SettingsState';
import { UIButton } from '../ui/UIButton';
import { UISlider } from '../ui/UISlider';
import { UIText } from '../ui/UIText';
import { UIPanel } from '../ui/UIPanel';

interface SettingsSceneData {
  returnTo?: string;
}

type SettingsElement = UISlider | UIButton;

export class SettingsScene extends Phaser.Scene {
  private returnTo: string = SCENES.MENU;
  private elements: SettingsElement[] = [];
  private selectedIndex: number = 0;

  // UI Elements
  private settingsPanel!: UIPanel;
  private masterSlider!: UISlider;
  private musicSlider!: UISlider;
  private sfxSlider!: UISlider;
  private playerDeviceButtons: Map<PlayerId, UIButton> = new Map();
  private backButton!: UIButton;

  constructor() {
    super({ key: SCENES.SETTINGS });
  }

  init(data: SettingsSceneData): void {
    this.returnTo = data.returnTo ?? SCENES.MENU;
  }

  create(): void {
    console.log('[SettingsScene] Creating settings...');
    SceneRouter.setCurrentScene(SCENES.SETTINGS);
    InputRouter.init(this);
    InputRouter.setMenuMode(true); // Higher deadzone for menu navigation

    AudioRouter.startMenuMusic(this);

    const settings = SettingsState.get();

    // Content dimensions for height-responsive panel
    const volumeSpacing = 60;
    const playerSpacing = 45;
    const titleY = 80;
    const contentStartY = 160;
    const backButtonHeight = 44;
    const bottomPadding = 50;
    const panelPaddingTop = 80;
    const panelPaddingBottom = 50;
    const contentHeight =
      (contentStartY - titleY) +
      3 * volumeSpacing +
      15 +
      4 * playerSpacing +
      15 +
      backButtonHeight +
      bottomPadding;
    const panelHeight = panelPaddingTop + contentHeight + panelPaddingBottom;
    const panelWidth = 550;
    const centerX = DESIGN_WIDTH / 2;
    const centerY = DESIGN_HEIGHT / 2;

    this.settingsPanel = new UIPanel(this, {
      x: centerX,
      y: centerY,
      width: panelWidth,
      height: panelHeight,
      title: 'Settings',
    });
    this.settingsPanel.setDepth(DEPTH.UI_BACKGROUND);

    // Title (above panel content, on top of panel)
    const titleText = new UIText(this, {
      x: centerX,
      y: titleY,
      text: 'SETTINGS',
      fontSize: 36,
      color: COLORS.ACCENT,
      fontStyle: 'bold',
    });
    titleText.setDepth(DEPTH.UI_BACKGROUND + 1);

    let yPos = contentStartY;

    const uiDepth = DEPTH.UI_BACKGROUND + 1;

    // Master Volume
    this.masterSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'Master Volume',
      value: settings.masterVolume,
      onChange: (value) => SettingsState.set('masterVolume', value),
    });
    this.masterSlider.setDepth(uiDepth);
    yPos += volumeSpacing;

    // Music Volume
    this.musicSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'Music Volume',
      value: settings.musicVolume,
      onChange: (value) => SettingsState.set('musicVolume', value),
    });
    this.musicSlider.setDepth(uiDepth);
    yPos += volumeSpacing;

    // SFX Volume
    this.sfxSlider = new UISlider(this, {
      x: centerX,
      y: yPos,
      label: 'SFX Volume',
      value: settings.sfxVolume,
      onChange: (value) => SettingsState.set('sfxVolume', value),
    });
    this.sfxSlider.setDepth(uiDepth);
    yPos += volumeSpacing + 15;

    // Player Input Devices (all 4 players)
    const players: PlayerId[] = ['P1', 'P2', 'P3', 'P4'];

    players.forEach((playerId, index) => {
      const label = new UIText(this, {
        x: centerX - 120,
        y: yPos,
        text: `Player ${index + 1}:`,
        fontSize: 16,
        align: 'right',
      });
      label.setDepth(uiDepth);

      const btn = new UIButton(this, {
        x: centerX + 80,
        y: yPos,
        width: 180,
        height: 35,
        text: this.formatDeviceText(playerId),
        fontSize: 14,
        onClick: () => this.cycleDevice(playerId),
      });
      btn.setDepth(uiDepth);
      this.playerDeviceButtons.set(playerId, btn);
      yPos += playerSpacing;
    });

    yPos += 15;

    // Back button
    this.backButton = new UIButton(this, {
      x: centerX,
      y: yPos,
      text: 'Back',
      onClick: () => this.goBack(),
    });
    this.backButton.setDepth(uiDepth);

    // Build elements array for navigation
    this.elements = [
      this.masterSlider,
      this.musicSlider,
      this.sfxSlider,
      ...players.map(p => this.playerDeviceButtons.get(p)!),
      this.backButton,
    ];

    this.updateSelection();
  }

  private formatDeviceText(player: PlayerId): string {
    const settings = SettingsState.getPlayerInput(player);
    if (settings.device === 'keyboard') {
      if (player === 'P1') return 'Keyboard (WASD)';
      if (player === 'P2') return 'Keyboard (Arrows)';
      return 'Keyboard';
    } else {
      return `Gamepad ${settings.gamepadIndex + 1}`;
    }
  }

  private cycleDevice(player: PlayerId): void {
    const settings = SettingsState.getPlayerInput(player);
    const connectedPads = InputRouter.getConnectedGamepadCount();

    if (settings.device === 'keyboard') {
      // Switch to gamepad 0
      SettingsState.setPlayerInput(player, { device: 'gamepad', gamepadIndex: 0 });
    } else if (settings.gamepadIndex < connectedPads - 1) {
      // Cycle to next gamepad
      SettingsState.setPlayerInput(player, { device: 'gamepad', gamepadIndex: settings.gamepadIndex + 1 });
    } else {
      // Back to keyboard
      SettingsState.setPlayerInput(player, { device: 'keyboard', gamepadIndex: 0 });
    }

    // Update button text
    const btn = this.playerDeviceButtons.get(player);
    if (btn) {
      btn.setText(this.formatDeviceText(player));
    }
  }

  private navigateUp(): void {
    this.selectedIndex = (this.selectedIndex - 1 + this.elements.length) % this.elements.length;
    this.updateSelection();
  }

  private navigateDown(): void {
    this.selectedIndex = (this.selectedIndex + 1) % this.elements.length;
    this.updateSelection();
  }

  private adjustLeft(): void {
    const element = this.elements[this.selectedIndex];
    if (element instanceof UISlider) {
      element.decrement(0.05);
    }
  }

  private adjustRight(): void {
    const element = this.elements[this.selectedIndex];
    if (element instanceof UISlider) {
      element.increment(0.05);
    }
  }

  private updateSelection(): void {
    this.elements.forEach((el, i) => {
      el.setSelected(i === this.selectedIndex);
    });
  }

  private confirmSelection(): void {
    const element = this.elements[this.selectedIndex];
    if (element instanceof UIButton) {
      element.click();
    }
  }

  private goBack(): void {
    SceneRouter.returnToPrevious(SCENES.SETTINGS, this.returnTo);
  }

  update(): void {
    InputRouter.update();

    // Handle navigation with new action-based input
    if (InputRouter.anyPlayerJustPressed('MOVE_UP')) {
      this.navigateUp();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_DOWN')) {
      this.navigateDown();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_LEFT')) {
      this.adjustLeft();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_RIGHT')) {
      this.adjustRight();
    }
    if (InputRouter.anyPlayerJustPressed('INTERACT')) {
      this.confirmSelection();
    }
    if (InputRouter.anyPlayerJustPressed('CANCEL') || InputRouter.anyPlayerJustPressed('PAUSE')) {
      this.goBack();
    }

    // Must call lateUpdate at end of frame to properly track "just pressed" state
    InputRouter.lateUpdate();
  }
}
